import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import DialogComponent from '../../../component/dialog/dialog.component';
import { CustomerLoading } from '../../../component/loading/loading';
import i18n from '../../../i18n';
import apiManage from '../../../request';
import {
    DataAnalysisAiSummaryRequest,
    DataAnalysisAiSummaryResponse,
} from '../../../request/api/data-analysis';

/**
 * 智能分析弹窗属性。
 */
interface DishAiAnalysisDialogProps {
    /** 弹窗开关。 */
    open: boolean;
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 本次请求参数。 */
    requestParams: DataAnalysisAiSummaryRequest | null;
}

/**
 * 智能分析展示结构。
 */
interface DishAiAnalysisDisplay {
    /** 标题。 */
    headline: string;
    /** 要点列表。 */
    points: string[];
    /** 总结文本。 */
    summary: string;
}

/**
 * 菜品分析智能总结弹窗。
 * @param props - 弹窗属性
 * @returns 智能总结弹窗
 */
function DishAiAnalysisDialog({ open, onClose, requestParams }: DishAiAnalysisDialogProps) {
    /** 加载状态。 */
    const [loading, setLoading] = useState(false);
    /** 错误文案。 */
    const [errorText, setErrorText] = useState('');
    /** 智能分析结果。 */
    const [analysisData, setAnalysisData] = useState<DishAiAnalysisDisplay | null>(null);
    /** 请求序号，避免旧请求覆盖新结果。 */
    const requestSeqRef = useRef(0);

    /**
     * 打开弹窗后拉取智能分析。
     */
    useEffect(() => {
        if (!open || !requestParams) {
            return;
        }
        void fetchAiSummary(requestParams);
    }, [open, requestParams]);

    /**
     * 拉取智能分析结果。
     * @param params - AI 请求参数
     */
    async function fetchAiSummary(params: DataAnalysisAiSummaryRequest) {
        /** 当前请求序号。 */
        const requestId = ++requestSeqRef.current;
        setLoading(true);
        setErrorText('');
        setAnalysisData(null);
        try {
            /** AI 分析接口响应。 */
            const response = await apiManage.post_data_analysis_ai_summary(params);
            if (requestId !== requestSeqRef.current) {
                return;
            }
            if (response?.error) {
                setErrorText(i18n.t('global_text_operate_failure'));
                return;
            }
            /** 结构化分析内容。 */
            const structuredAnalysis = (response as DataAnalysisAiSummaryResponse)?.analysis;
            if (structuredAnalysis && typeof structuredAnalysis === 'object') {
                setAnalysisData({
                    headline: structuredAnalysis.headline || '',
                    points: Array.isArray(structuredAnalysis.points) ? structuredAnalysis.points : [],
                    summary: structuredAnalysis.summary || '',
                });
                return;
            }
            /** 兜底原始文本。 */
            const rawText = String((response as DataAnalysisAiSummaryResponse)?.raw_text || '').trim();
            if (rawText) {
                setAnalysisData({
                    headline: '',
                    points: [],
                    summary: rawText,
                });
                return;
            }
            setErrorText(i18n.t('general_text_no_data'));
        } catch (error) {
            if (requestId !== requestSeqRef.current) {
                return;
            }
            setErrorText(i18n.t('global_text_operate_failure'));
        } finally {
            if (requestId === requestSeqRef.current) {
                setLoading(false);
            }
        }
    }

    /**
     * 分析是否为空。
     * @returns 是否没有可展示内容
     */
    const isEmptyAnalysis = useMemo(() => {
        return !analysisData
            || (!analysisData.headline && analysisData.points.length === 0 && !analysisData.summary);
    }, [analysisData]);

    /**
     * 渲染单张分析卡片。
     * @param title - 卡片标题
     * @param content - 卡片内容
     * @returns 分析卡片
     */
    const renderAnalysisCard = (title: string, content: React.ReactNode) => {
        return (
            <Box
                sx={{
                    bgcolor: 'var(--fill-neutral-100)',
                    borderRadius: '16px',
                    boxShadow: '0px 0px 4px var(--shadow-25)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    p: '16px',
                }}
            >
                <Typography
                    variant='subtitle1'
                    sx={{ color: 'var(--text-primary-300)', mb: '8px', textAlign: 'center' }}
                >
                    {title}
                </Typography>
                {content}
            </Box>
        );
    };

    return (
        <DialogComponent
            open={open}
            onClose={onClose}
            defaultHeaderProps={{
                title: i18n.t('intelligent_risk_control_page_text_data_intelligence_analysis'),
                titleAlign: 'center',
            }}
            maxWidth="md"
            heightSize="large"
            sx={{
                '.MuiDialog-container > .MuiPaper-root': {
                    width: 760,
                    maxWidth: '90vw',
                    minHeight: 420,
                },
                '.MuiDialogContent-root': {
                    padding: '8px 24px 24px 24px',
                },
                '.MuiDialogActions-root': {
                    display: 'none',
                },
            }}
        >
            {loading ? (
                <CustomerLoading />
            ) : null}
            {!loading && errorText ? (
                <Alert severity="error" sx={{ m: 2 }}>{errorText}</Alert>
            ) : null}
            {!loading && !errorText && !isEmptyAnalysis && analysisData ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gridTemplateAreas: {
                            xs: `
                                "headline"
                                "points"
                                "summary"
                            `,
                            md: `
                                "headline points"
                                "summary points"
                            `,
                        },
                        gridTemplateRows: { xs: 'auto', md: '1fr 1fr' },
                        height: '100%',
                        gap: 2,
                    }}
                >
                    <Box sx={{ gridArea: 'headline' }}>
                        {renderAnalysisCard(
                        i18n.t('business_analysis_page_ai_analysis_card_headline'),
                        <Typography variant='body2' sx={{ color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                            {analysisData.headline || i18n.t('general_text_no_data')}
                        </Typography>
                    )}
                    </Box>
                    <Box sx={{ gridArea: 'summary' }}>
                        {renderAnalysisCard(
                        i18n.t('business_analysis_page_ai_analysis_card_summary'),
                        <Typography variant='body2' sx={{ color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                            {analysisData.summary || i18n.t('general_text_no_data')}
                        </Typography>
                    )}
                    </Box>
                    <Box sx={{ gridArea: 'points' }}>
                        {renderAnalysisCard(
                        i18n.t('business_analysis_page_ai_analysis_card_points'),
                        analysisData.points.length > 0 ? (
                            <Stack spacing={1}>
                                {analysisData.points.map((point, index) => (
                                    <Typography key={`${point}-${index}`} variant='body2' sx={{ color: 'var(--text-dark)' }}>
                                        {`${index + 1}. ${point}`}
                                    </Typography>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant='body2' sx={{ color: 'var(--text-dark)' }}>
                                {i18n.t('general_text_no_data')}
                            </Typography>
                        )
                    )}
                    </Box>
                </Box>
            ) : null}
            {!loading && !errorText && isEmptyAnalysis ? (
                <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography>{i18n.t('general_text_no_data')}</Typography>
                </Box>
            ) : null}
        </DialogComponent>
    );
}

export default DishAiAnalysisDialog;
